import { z } from 'zod';

/**
 * AI availability state for the caller in their active team — the single source
 * of truth the web app renders every AI surface from (W7.1).
 *
 *  - `enabled`        platform on + the team/user is entitled → full AI.
 *  - `upsell`         platform on, but not entitled (Free / plan without AI) →
 *                     surfaces shown *locked* with an upgrade CTA.
 *  - `unavailable`    platform off (FEATURE_AI off or no AI-core URL) → surfaces
 *                     cleanly hidden (a solid task manager, no broken blocks).
 *  - `quota_reached`  entitled but the monthly AI cost-cap is hit (W7.4).
 */
export const AiStatusStateSchema = z.enum(['enabled', 'upsell', 'unavailable', 'quota_reached']);
export type AiStatusState = z.infer<typeof AiStatusStateSchema>;

/**
 * AI feature groups — tiered AI access per docs/24-MONETIZATION.md §3.
 *  - `A`  Personal helpers: Smart Create (Cmd+K), /ai assistant, daily digest,
 *         smart search, standup, comment improver, voice-to-task, summarizer.
 *  - `B`  Team helpers: team risk analysis, status drafts, planning assistant,
 *         meeting-notes→tasks, PR summaries, quality gates, email-to-task.
 *  - `C`  AI agents: Reviewer/PM/Onboarding bots + custom agents.
 *  - `D`  Deep integrations: MCP server (Claude Code/Cursor/Cline), send-to-CC,
 *         dashboard AI insights, background embeddings for semantic search.
 */
export const AiGroupSchema = z.enum(['A', 'B', 'C', 'D']);
export type AiGroup = z.infer<typeof AiGroupSchema>;

/**
 * Canonical plan → AI groups mapping (founder-locked 2026-06-08).  THE single
 * source of truth for AI tiering — both API gating and the web app import this.
 *
 * `billing.plans.features.ai` (migration 0102) gates AI on/off per plan; this
 * map differentiates WHICH groups each AI-bearing plan unlocks.  Keep the two in
 * sync: a plan with no groups here should also have `features.ai = false`.
 */
export const PLAN_AI_GROUPS: Readonly<Record<string, readonly AiGroup[]>> = {
  free: [],
  personal_pro: ['A'],
  team: ['A', 'B', 'C', 'D'],
  enterprise: ['A', 'B', 'C', 'D'],
};

/** AI groups unlocked by a plan code.  Unknown / nullish plan → none. */
export function aiGroupsForPlan(planCode: string | null | undefined): AiGroup[] {
  if (!planCode) return [];
  return [...(PLAN_AI_GROUPS[planCode] ?? [])];
}

/** Whether a plan code unlocks a given AI group. */
export function planHasAiGroup(planCode: string | null | undefined, group: AiGroup): boolean {
  return aiGroupsForPlan(planCode).includes(group);
}

export const AiStatusResponseSchema = z.object({
  state: AiStatusStateSchema,
  /**
   * Back-compat boolean — `true` iff `state === 'enabled'`.  Existing gates read
   * `aiStatus.enabled`; keep it so they don't all have to change at once.
   */
  enabled: z.boolean(),
  /** The plan that unlocks AI, present when `state === 'upsell'`. */
  requiredPlan: z.string().optional(),
  /**
   * Why AI is `unavailable`, so the UI can name the next step instead of a
   * dead end: `platform_off` = this server has AI switched off (operator);
   * `no_permission` = the caller's role in this team has no AI permission
   * (a team owner / manager can grant it).
   */
  reason: z.enum(['platform_off', 'no_permission']).optional(),
  /**
   * AI feature groups the caller is entitled to in the active context — the
   * union of the active team's plan groups and the user's personal plan groups
   * (see PLAN_AI_GROUPS).  Empty unless AI is actually usable (`enabled` /
   * `quota_reached`).  The web app gates Group B/C/D surfaces on membership;
   * any AI at all implies Group A.
   */
  aiGroups: z.array(AiGroupSchema).default([]),
  /**
   * Monthly Managed-AI usage — present ONLY when the active team runs on Managed
   * mode (we pay the tokens).  Drives the "X of N requests used" meter and the
   * soft-stop copy.  Absent for BYOK (customer's own key = no budget).
   *
   * Deliberately carries NO dollar figures (founder-locked) — usage is shown to
   * customers as requests + a percentage only; the $ accounting stays server-side.
   */
  managedBudget: z
    .object({
      /** Exact: spent ÷ budget, 0–100. */
      accounting: z.literal('estimate_including_reservations').optional(),
      usedPct: z.number(),
      /** Requests used / included this month (dollars rendered as a request count). */
      requestsUsed: z.number(),
      requestsLimit: z.number(),
      /** ISO timestamp — start of next UTC month, when usage resets. */
      resetsAt: z.string(),
    })
    .optional(),
});
export type AiStatusResponse = z.infer<typeof AiStatusResponseSchema>;
